import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { DICT } from "./i18n-dict";

export type Lang = "tr" | "en" | "de" | "fr" | "ar" | "es";
export const LANGS: { code: Lang; label: string }[] = [
  { code: "tr", label: "TR" },
  { code: "en", label: "EN" },
  { code: "de", label: "DE" },
  { code: "fr", label: "FR" },
  { code: "ar", label: "AR" },
  { code: "es", label: "ES" },
];

const STORAGE_KEY = "juen-lang";

interface I18nValue {
  lang: Lang;
  setLang: (l: Lang) => void;
  /** Translate a key. Falls back to Turkish, then to the key itself. */
  t: (key: string, vars?: Record<string, string | number>) => string;
  /** Locale string for Intl / toLocaleString */
  locale: string;
}

const I18nContext = createContext<I18nValue | undefined>(undefined);

const LOCALES: Record<Lang, string> = { tr: "tr-TR", en: "en-GB", de: "de-DE", fr: "fr-FR", ar: "ar", es: "es-ES" };

const RTL_LANGS: Lang[] = ["ar"];

function interpolate(text: string, vars?: Record<string, string | number>) {
  if (!vars) return text;
  return text.replace(/\{(\w+)\}/g, (_, k) => (vars[k] !== undefined ? String(vars[k]) : `{${k}}`));
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>("tr");

  useEffect(() => {
    try {
      const stored = window.localStorage.getItem(STORAGE_KEY) as Lang | null;
      if (stored && LANGS.some((l) => l.code === stored)) {
        setLangState(stored);
        return;
      }
      const nav = window.navigator.language?.slice(0, 2).toLowerCase();
      if (nav === "de") setLangState("de");
      else if (nav === "en") setLangState("en");
      else if (nav === "fr") setLangState("fr");
      else if (nav === "ar") setLangState("ar");
      else if (nav === "es") setLangState("es");
    } catch {
      /* noop */
    }
  }, []);

  useEffect(() => {
    if (typeof document !== "undefined") {
      document.documentElement.lang = lang;
      document.documentElement.dir = RTL_LANGS.includes(lang) ? "rtl" : "ltr";
    }
  }, [lang]);

  const setLang = useCallback((l: Lang) => {
    setLangState(l);
    try {
      window.localStorage.setItem(STORAGE_KEY, l);
    } catch {
      /* noop */
    }
  }, []);

  const t = useCallback(
    (key: string, vars?: Record<string, string | number>) => {
      const entry = DICT[key];
      if (!entry) return interpolate(key, vars);
      return interpolate(entry[lang] ?? entry.tr ?? key, vars);
    },
    [lang],
  );

  const value = useMemo<I18nValue>(() => ({ lang, setLang, t, locale: LOCALES[lang] }), [lang, setLang, t]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nValue {
  const ctx = useContext(I18nContext);
  if (!ctx) {
    // Safe fallback so components can render outside the provider (e.g. tests).
    return {
      lang: "tr",
      setLang: () => {},
      t: (key, vars) => interpolate(DICT[key]?.tr ?? key, vars),
      locale: "tr-TR",
    };
  }
  return ctx;
}
